import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Globe, XCircle, Pencil, Trash2, Briefcase, HelpCircle, X, GripVertical, Wand2, Sparkles, Loader2 } from "lucide-react";
import { recruitmentAPI, apiClient } from "../services/api";
import { useCountryConfig } from "../hooks/useCountryConfig";
import { toast } from "sonner";

// Inline SVG logos (LinkedIn + Indeed n'existent pas dans lucide)
const LinkedInIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6zM2 9h4v12H2z"/>
    <circle cx="4" cy="4" r="2"/>
  </svg>
);

const IndeedIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14H9V10h2v6zm0-8H9V6h2v2zm4 8h-2v-3.5c0-.83-.67-1.5-1.5-1.5S10 13.17 10 14v2H8v-6h2v.93c.5-.7 1.29-1.43 2.5-1.43C14.43 9.5 15 11 15 12.5V16z"/>
  </svg>
);

const STATUS_LABELS = {
  draft:     { color: "var(--lm-ink-3)",  bg: "var(--lm-s2)" },
  published: { color: "#16a34a",          bg: "rgba(22,163,74,0.10)" },
  closed:    { color: "var(--lm-ink-3)",  bg: "var(--lm-s3)" },
};

const EMPTY_FORM = {
  title: "", description: "", requirements: "",
  contract_type: "", currency: "TND",
  salary_min: "", salary_max: "",
  experience_required: "", country_code: "", language: "fr",
};

export default function OffresEmploi() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  // Default new offers to the tenant's country/currency instead of a hardcoded
  // TND — otherwise a Qatar/Gulf tenant creates offers (and shows them on the
  // public careers portal) priced in Tunisian Dinars.
  const { config } = useCountryConfig();
  const newOfferDefaults = () => ({
    ...EMPTY_FORM,
    currency: config?.currency?.code || EMPTY_FORM.currency,
    country_code: config?.code || EMPTY_FORM.country_code,
  });
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [filterStatus, setFilterStatus] = useState("");
  const [questionsPosting, setQuestionsPosting] = useState(null);
  const [showAiGen, setShowAiGen] = useState(false);

  const { data: postings = [], isLoading } = useQuery({
    queryKey: ["job-postings", filterStatus],
    queryFn: () => recruitmentAPI.listPostings(filterStatus ? { status: filterStatus } : {}).then((r) => r.data),
  });

  const createMut = useMutation({
    mutationFn: (data) => recruitmentAPI.createPosting(data),
    onSuccess: () => { qc.invalidateQueries(["job-postings"]); closeForm(); },
  });

  const updateMut = useMutation({
    mutationFn: ({ id, data }) => recruitmentAPI.updatePosting(id, data),
    onSuccess: () => { qc.invalidateQueries(["job-postings"]); closeForm(); },
  });

  const publishMut = useMutation({
    mutationFn: (id) => recruitmentAPI.publishPosting(id),
    onSuccess: () => qc.invalidateQueries(["job-postings"]),
  });

  const closeMut = useMutation({
    mutationFn: (id) => recruitmentAPI.closePosting(id),
    onSuccess: () => qc.invalidateQueries(["job-postings"]),
  });

  const deleteMut = useMutation({
    mutationFn: (id) => recruitmentAPI.deletePosting(id),
    onSuccess: () => qc.invalidateQueries(["job-postings"]),
  });

  const linkedinMut = useMutation({
    mutationFn: (id) => recruitmentAPI.publishToLinkedIn(id),
    onSuccess: (_, id) => { qc.invalidateQueries(["job-postings"]); toast.success(t("offers.toasts.publishedLinkedin")); },
    onError: (err) => toast.error(err?.response?.data?.detail || t("offers.toasts.errorLinkedin")),
  });

  const indeedMut = useMutation({
    mutationFn: (id) => recruitmentAPI.publishToIndeed(id),
    onSuccess: () => { qc.invalidateQueries(["job-postings"]); toast.success(t("offers.toasts.publishedIndeed")); },
    onError: (err) => toast.error(err?.response?.data?.detail || t("offers.toasts.errorIndeed")),
  });

  const unpublishExtMut = useMutation({
    mutationFn: ({ id, platform }) => recruitmentAPI.unpublishExternal(id, platform),
    onSuccess: (_, { platform }) => { qc.invalidateQueries(["job-postings"]); toast.success(t("offers.toasts.removedFrom", { platform })); },
    onError: (err) => toast.error(err?.response?.data?.detail || t("common.error")),
  });

  const { data: extStatus } = useQuery({
    queryKey: ["external-publish-status"],
    queryFn: () => recruitmentAPI.getExternalStatus().then((r) => r.data),
    staleTime: 60_000,
  });

  const openCreate = () => { setEditing(null); setForm(newOfferDefaults()); setShowForm(true); };

  // Soft-link from an open position ("Recruter" button in PositionsHub): the
  // position passes an offer seed via router state. Open the create form
  // pre-filled, then clear the state so a refresh/back doesn't re-trigger it.
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const prefill = location.state?.prefillOffer;
    if (!prefill) return;
    setEditing(null);
    setForm({ ...newOfferDefaults(), ...prefill });
    setShowForm(true);
    navigate(`${location.pathname}${location.search}`, { replace: true, state: {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);
  const openEdit = (p) => {
    setEditing(p.id);
    setForm({
      title: p.title, description: p.description, requirements: p.requirements || "",
      contract_type: p.contract_type || "", currency: p.currency || "TND",
      salary_min: p.salary_min || "", salary_max: p.salary_max || "",
      experience_required: p.experience_required || "",
      country_code: p.country_code || "", language: p.language || "fr",
    });
    setShowForm(true);
  };
  const closeForm = () => { setShowForm(false); setEditing(null); setForm(EMPTY_FORM); };

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      salary_min: form.salary_min ? parseFloat(form.salary_min) : null,
      salary_max: form.salary_max ? parseFloat(form.salary_max) : null,
    };
    if (editing) updateMut.mutate({ id: editing, data: payload });
    else createMut.mutate(payload);
  };

  return (
    <div className="space-y-5">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex gap-2">
          {["", "draft", "published", "closed"].map((s) => (
            <button
              key={s}
              onClick={() => setFilterStatus(s)}
              className="px-3 py-1.5 rounded-full text-xs font-medium transition-all"
              style={{
                backgroundColor: filterStatus === s ? "#5e6ad2" : "var(--lm-s2)",
                color: filterStatus === s ? "#fff" : "var(--lm-ink-2)",
                border: `1px solid ${filterStatus === s ? "#5e6ad2" : "var(--lm-border)"}`,
              }}
            >
              {s === "" ? t("offers.allFilter") : t(`offers.status.${s}`)}
            </button>
          ))}
        </div>
        <button
          onClick={openCreate}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all"
          style={{ backgroundColor: "#5e6ad2", color: "#fff", border: "1px solid #5e6ad2" }}
        >
          <Plus className="h-4 w-4" />
          {t("offers.newOffer")}
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <div
          className="rounded-xl p-6 space-y-4"
          style={{ backgroundColor: "var(--lm-s2)", border: "1px solid var(--lm-border)" }}
        >
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-sm" style={{ color: "var(--lm-ink)" }}>
              {editing ? t("offers.editTitle") : t("offers.createTitle")}
            </h3>
            <button onClick={closeForm} style={{ color: "var(--lm-ink-3)" }}>
              <XCircle className="h-4 w-4" />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-xs font-medium mb-1" style={{ color: "var(--lm-ink-2)" }}>
                  {t("offers.form.title")}
                </label>
                <input
                  required
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg text-sm"
                  style={{ backgroundColor: "var(--lm-s1)", border: "1px solid var(--lm-border)", color: "var(--lm-ink)" }}
                  placeholder={t("offers.form.titlePh")}
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: "var(--lm-ink-2)" }}>{t("offers.form.contractType")}</label>
                <input
                  value={form.contract_type}
                  onChange={(e) => setForm((f) => ({ ...f, contract_type: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg text-sm"
                  style={{ backgroundColor: "var(--lm-s1)", border: "1px solid var(--lm-border)", color: "var(--lm-ink)" }}
                  placeholder={t("offers.form.contractPh")}
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: "var(--lm-ink-2)" }}>{t("offers.form.experience")}</label>
                <input
                  value={form.experience_required}
                  onChange={(e) => setForm((f) => ({ ...f, experience_required: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg text-sm"
                  style={{ backgroundColor: "var(--lm-s1)", border: "1px solid var(--lm-border)", color: "var(--lm-ink)" }}
                  placeholder={t("offers.form.experiencePh")}
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: "var(--lm-ink-2)" }}>{t("offers.form.salaryMin")}</label>
                <input
                  type="number" min="0"
                  value={form.salary_min}
                  onChange={(e) => setForm((f) => ({ ...f, salary_min: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg text-sm"
                  style={{ backgroundColor: "var(--lm-s1)", border: "1px solid var(--lm-border)", color: "var(--lm-ink)" }}
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: "var(--lm-ink-2)" }}>{t("offers.form.salaryMax")}</label>
                <input
                  type="number" min="0"
                  value={form.salary_max}
                  onChange={(e) => setForm((f) => ({ ...f, salary_max: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg text-sm"
                  style={{ backgroundColor: "var(--lm-s1)", border: "1px solid var(--lm-border)", color: "var(--lm-ink)" }}
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: "var(--lm-ink-2)" }}>{t("offers.form.currency")}</label>
                <select
                  value={form.currency}
                  onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg text-sm"
                  style={{ backgroundColor: "var(--lm-s1)", border: "1px solid var(--lm-border)", color: "var(--lm-ink)" }}
                >
                  {["TND", "EUR", "USD", "QAR", "SAR", "MAD"].map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: "var(--lm-ink-2)" }}>{t("offers.form.country")}</label>
                <input
                  value={form.country_code}
                  onChange={(e) => setForm((f) => ({ ...f, country_code: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg text-sm"
                  style={{ backgroundColor: "var(--lm-s1)", border: "1px solid var(--lm-border)", color: "var(--lm-ink)" }}
                  placeholder={t("offers.form.countryPh")}
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: "var(--lm-ink-2)" }}>{t("offers.form.language")}</label>
                <select
                  value={form.language}
                  onChange={(e) => setForm((f) => ({ ...f, language: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg text-sm"
                  style={{ backgroundColor: "var(--lm-s1)", border: "1px solid var(--lm-border)", color: "var(--lm-ink)" }}
                >
                  <option value="fr">{t("languageSelector.fr")}</option>
                  <option value="en">{t("languageSelector.en")}</option>
                  <option value="ar">{t("languageSelector.ar")}</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-medium" style={{ color: "var(--lm-ink-2)" }}>{t("offers.form.description")}</label>
                  <button type="button" onClick={() => setShowAiGen(true)}
                    className="flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-md transition-colors"
                    style={{ color: "#5e6ad2", border: "1px solid #5e6ad2", background: "rgba(94,106,210,0.06)" }}>
                    <Sparkles className="h-3 w-3" /> {t("offers.form.genAi")}
                  </button>
                </div>
                <textarea
                  required rows={4}
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg text-sm resize-none"
                  style={{ backgroundColor: "var(--lm-s1)", border: "1px solid var(--lm-border)", color: "var(--lm-ink)" }}
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-medium mb-1" style={{ color: "var(--lm-ink-2)" }}>{t("offers.form.requirements")}</label>
                <textarea
                  rows={3}
                  value={form.requirements}
                  onChange={(e) => setForm((f) => ({ ...f, requirements: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg text-sm resize-none"
                  style={{ backgroundColor: "var(--lm-s1)", border: "1px solid var(--lm-border)", color: "var(--lm-ink)" }}
                />
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <button type="button" onClick={closeForm} className="px-4 py-2 rounded-lg text-sm"
                style={{ backgroundColor: "var(--lm-s1)", color: "var(--lm-ink-2)", border: "1px solid var(--lm-border)" }}>
                {t("common.cancel")}
              </button>
              <button type="submit" className="px-4 py-2 rounded-lg text-sm font-medium"
                style={{ backgroundColor: "#5e6ad2", color: "#fff", border: "1px solid #5e6ad2" }}>
                {editing ? t("common.save") : t("offers.form.create")}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* List */}
      {isLoading ? (
        <div className="py-12 text-center text-sm" style={{ color: "var(--lm-ink-3)" }}>{t("common.loading")}</div>
      ) : postings.length === 0 ? (
        <div className="py-16 flex flex-col items-center gap-3" style={{ color: "var(--lm-ink-3)" }}>
          <Briefcase className="h-10 w-10" />
          <p className="text-sm">{t("offers.empty")}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {postings.map((p) => {
            const st = STATUS_LABELS[p.status] || STATUS_LABELS.draft;
            return (
              <div
                key={p.id}
                className="rounded-xl px-5 py-4 flex items-start gap-4"
                style={{ backgroundColor: "var(--lm-s1)", border: "1px solid var(--lm-border)" }}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm" style={{ color: "var(--lm-ink)" }}>{p.title}</span>
                    <span
                      className="text-xs px-2 py-0.5 rounded-full font-medium"
                      style={{ color: st.color, backgroundColor: st.bg }}
                    >
                      {t(`offers.status.${p.status}`, { defaultValue: p.status })}
                    </span>
                    {p.country_code && (
                      <span className="text-xs" style={{ color: "var(--lm-ink-3)" }}>{p.country_code}</span>
                    )}
                  </div>
                  <p className="text-xs mt-1 line-clamp-1" style={{ color: "var(--lm-ink-2)" }}>
                    {p.contract_type && <span className="mr-3">{p.contract_type}</span>}
                    {(p.salary_min || p.salary_max) && (
                      <span>
                        {p.salary_min && Number(p.salary_min).toLocaleString()}{p.salary_max && ` — ${Number(p.salary_max).toLocaleString()}`} {p.currency}
                      </span>
                    )}
                  </p>
                  <p className="text-xs mt-0.5" style={{ color: "var(--lm-ink-3)" }}>
                    {t("offers.applications", { count: p.application_count })}
                  </p>
                  {/* External channel badges */}
                  {(p.linkedin_job_id || p.indeed_job_id) && (
                    <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                      {p.linkedin_job_id && (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs"
                          style={{ backgroundColor: "rgba(10,102,194,0.12)", color: "#0a66c2", border: "1px solid rgba(10,102,194,0.25)" }}
                        >
                          <LinkedInIcon size={11} /> LinkedIn
                        </span>
                      )}
                      {p.indeed_job_id && (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs"
                          style={{ backgroundColor: "rgba(37,87,167,0.12)", color: "#2557a7", border: "1px solid rgba(37,87,167,0.25)" }}
                        >
                          <IndeedIcon size={11} /> Indeed
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {p.status === "draft" && (
                    <ActionBtn icon={Globe} title={t("offers.actions.publish")} onClick={() => publishMut.mutate(p.id)} color="#16a34a" />
                  )}
                  {p.status === "published" && (
                    <>
                      {/* LinkedIn publish/unpublish */}
                      {!p.linkedin_job_id ? (
                        <button
                          title={extStatus?.linkedin?.configured ? t("offers.actions.publishLinkedin") : t("offers.actions.linkedinNotConfigured")}
                          onClick={() => { if (extStatus?.linkedin?.configured) linkedinMut.mutate(p.id); else toast.error(t("offers.actions.linkedinNotConfiguredMsg")); }}
                          className="p-1.5 rounded-lg transition-colors"
                          style={{ color: extStatus?.linkedin?.configured ? "#0a66c2" : "var(--lm-ink-3)" }}
                          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "rgba(10,102,194,0.10)"; }}
                          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; }}
                        >
                          <LinkedInIcon size={15} />
                        </button>
                      ) : (
                        <button
                          title={t("offers.actions.removeLinkedin")}
                          onClick={() => unpublishExtMut.mutate({ id: p.id, platform: "linkedin" })}
                          className="p-1.5 rounded-lg transition-colors"
                          style={{ color: "#0a66c2" }}
                          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "rgba(10,102,194,0.10)"; }}
                          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; }}
                        >
                          <LinkedInIcon size={15} />
                        </button>
                      )}
                      {/* Indeed publish/unpublish */}
                      {!p.indeed_job_id ? (
                        <button
                          title={extStatus?.indeed?.configured ? t("offers.actions.publishIndeed") : t("offers.actions.indeedNotConfigured")}
                          onClick={() => { if (extStatus?.indeed?.configured) indeedMut.mutate(p.id); else toast.error(t("offers.actions.indeedNotConfiguredMsg")); }}
                          className="p-1.5 rounded-lg transition-colors"
                          style={{ color: extStatus?.indeed?.configured ? "#2557a7" : "var(--lm-ink-3)" }}
                          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "rgba(37,87,167,0.10)"; }}
                          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; }}
                        >
                          <IndeedIcon size={15} />
                        </button>
                      ) : (
                        <button
                          title={t("offers.actions.removeIndeed")}
                          onClick={() => unpublishExtMut.mutate({ id: p.id, platform: "indeed" })}
                          className="p-1.5 rounded-lg transition-colors"
                          style={{ color: "#2557a7" }}
                          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "rgba(37,87,167,0.10)"; }}
                          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; }}
                        >
                          <IndeedIcon size={15} />
                        </button>
                      )}
                      <ActionBtn icon={XCircle} title={t("offers.actions.close")} onClick={() => closeMut.mutate(p.id)} color="var(--lm-ink-3)" />
                    </>
                  )}
                  <ActionBtn icon={HelpCircle} title={t("offers.actions.questions")} onClick={() => setQuestionsPosting(p)} color="#7c3aed" />
                  <ActionBtn icon={Pencil} title={t("offers.actions.edit")} onClick={() => openEdit(p)} color="var(--lm-ink-2)" />
                  {p.status !== "published" && (
                    <ActionBtn icon={Trash2} title={t("offers.actions.delete")} onClick={() => { if (window.confirm(t("offers.actions.deleteConfirm"))) deleteMut.mutate(p.id); }} color="#dc2626" />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {questionsPosting && (
        <QuestionsModal
          posting={questionsPosting}
          onClose={() => setQuestionsPosting(null)}
        />
      )}

      {showAiGen && (
        <AIGeneratorModal
          initialTitle={form.title}
          initialLanguage={form.language}
          initialContract={form.contract_type}
          onClose={() => setShowAiGen(false)}
          onApply={(result) => {
            setForm((f) => ({
              ...f,
              title: result.title || f.title,
              description: result.description_markdown || f.description,
              requirements: result.requirements_markdown || f.requirements,
            }));
            setShowAiGen(false);
            toast.success(t("offers.toasts.generated"));
          }}
        />
      )}
    </div>
  );
}

function AIGeneratorModal({ initialTitle, initialLanguage, initialContract, onClose, onApply }) {
  const { t } = useTranslation();
  const [roleTitle, setRoleTitle] = useState(initialTitle || "");
  const [seniority, setSeniority] = useState("mid");
  const [language, setLanguage] = useState(initialLanguage || "fr");
  const [contractType, setContractType] = useState(initialContract || "");
  const [location, setLocation] = useState("");
  const [companyContext, setCompanyContext] = useState("");
  const [keySkills, setKeySkills] = useState("");
  const [customInstructions, setCustomInstructions] = useState("");
  const [result, setResult] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);

  const { data: options } = useQuery({
    queryKey: ["ai-jd-options"],
    queryFn: () => apiClient.get("v1/recruitment/ai/job-description/options").then((r) => r.data),
    staleTime: 5 * 60 * 1000,
  });

  const generate = async () => {
    if (!roleTitle.trim()) {
      setError(t("offers.ai.titleRequired"));
      return;
    }
    setGenerating(true);
    setError(null);
    try {
      const r = await apiClient.post("v1/recruitment/ai/job-description", {
        role_title: roleTitle,
        seniority,
        language,
        contract_type: contractType || null,
        location: location || null,
        company_context: companyContext || null,
        key_skills: keySkills || null,
        custom_instructions: customInstructions || null,
      });
      setResult(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || t("offers.ai.genError"));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b flex items-center justify-between" style={{ borderColor: "var(--lm-border)" }}>
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5" style={{ color: "#5e6ad2" }} />
            <h2 className="text-lg font-semibold" style={{ color: "var(--lm-ink)" }}>{t("offers.ai.title")}</h2>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="overflow-y-auto p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Left — inputs */}
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: "var(--lm-ink-2)" }}>{t("offers.ai.roleTitle")}</label>
              <input value={roleTitle} onChange={(e) => setRoleTitle(e.target.value)}
                placeholder={t("offers.ai.roleTitlePh")}
                className="w-full px-3 py-2 rounded-lg text-sm"
                style={{ backgroundColor: "var(--lm-s1)", border: "1px solid var(--lm-border)", color: "var(--lm-ink)" }} />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: "var(--lm-ink-2)" }}>{t("offers.ai.seniority")}</label>
                <select value={seniority} onChange={(e) => setSeniority(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg text-sm"
                  style={{ backgroundColor: "var(--lm-s1)", border: "1px solid var(--lm-border)", color: "var(--lm-ink)" }}>
                  {(options?.seniority_levels || []).map((s) => (
                    <option key={s.id} value={s.id}>{s.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: "var(--lm-ink-2)" }}>{t("offers.ai.language")}</label>
                <select value={language} onChange={(e) => setLanguage(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg text-sm"
                  style={{ backgroundColor: "var(--lm-s1)", border: "1px solid var(--lm-border)", color: "var(--lm-ink)" }}>
                  {(options?.languages || []).map((l) => (
                    <option key={l.id} value={l.id}>{l.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: "var(--lm-ink-2)" }}>{t("offers.ai.contract")}</label>
                <input value={contractType} onChange={(e) => setContractType(e.target.value)}
                  placeholder={t("offers.ai.contractPh")}
                  className="w-full px-3 py-2 rounded-lg text-sm"
                  style={{ backgroundColor: "var(--lm-s1)", border: "1px solid var(--lm-border)", color: "var(--lm-ink)" }} />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: "var(--lm-ink-2)" }}>{t("offers.ai.location")}</label>
                <input value={location} onChange={(e) => setLocation(e.target.value)}
                  placeholder={t("offers.ai.locationPh")}
                  className="w-full px-3 py-2 rounded-lg text-sm"
                  style={{ backgroundColor: "var(--lm-s1)", border: "1px solid var(--lm-border)", color: "var(--lm-ink)" }} />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: "var(--lm-ink-2)" }}>{t("offers.ai.keySkills")}</label>
              <input value={keySkills} onChange={(e) => setKeySkills(e.target.value)}
                placeholder={t("offers.ai.keySkillsPh")}
                className="w-full px-3 py-2 rounded-lg text-sm"
                style={{ backgroundColor: "var(--lm-s1)", border: "1px solid var(--lm-border)", color: "var(--lm-ink)" }} />
            </div>

            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: "var(--lm-ink-2)" }}>{t("offers.ai.companyContext")}</label>
              <textarea rows={2} value={companyContext} onChange={(e) => setCompanyContext(e.target.value)}
                placeholder={t("offers.ai.companyContextPh")}
                className="w-full px-3 py-2 rounded-lg text-sm resize-none"
                style={{ backgroundColor: "var(--lm-s1)", border: "1px solid var(--lm-border)", color: "var(--lm-ink)" }} />
            </div>

            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: "var(--lm-ink-2)" }}>{t("offers.ai.instructions")}</label>
              <textarea rows={2} value={customInstructions} onChange={(e) => setCustomInstructions(e.target.value)}
                placeholder={t("offers.ai.instructionsPh")}
                className="w-full px-3 py-2 rounded-lg text-sm resize-none"
                style={{ backgroundColor: "var(--lm-s1)", border: "1px solid var(--lm-border)", color: "var(--lm-ink)" }} />
            </div>

            <button onClick={generate} disabled={generating || !roleTitle}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold text-white disabled:opacity-50"
              style={{ backgroundColor: "#5e6ad2" }}>
              {generating ? <><Loader2 className="h-4 w-4 animate-spin" /> {t("offers.ai.generating")}</> : <><Wand2 className="h-4 w-4" /> {t("offers.ai.generate")}</>}
            </button>

            {error && (
              <p className="text-xs text-red-600">{error}</p>
            )}
          </div>

          {/* Right — preview */}
          <div className="rounded-lg p-4 text-sm" style={{ backgroundColor: "var(--lm-s1)", border: "1px solid var(--lm-border)", color: "var(--lm-ink)" }}>
            {!result ? (
              <div className="h-full flex items-center justify-center text-center text-xs text-muted-foreground italic min-h-[400px]">
                {t("offers.ai.preview")}
              </div>
            ) : (
              <div className="space-y-3 max-h-[460px] overflow-y-auto pr-2">
                <h3 className="font-bold text-base">{result.title}</h3>
                {result.summary && <p className="text-xs italic text-muted-foreground">{result.summary}</p>}
                {result.responsibilities?.length > 0 && (
                  <div>
                    <p className="font-semibold text-xs mb-1">{t("offers.ai.missions")}</p>
                    <ul className="text-xs space-y-0.5 list-disc list-inside">
                      {result.responsibilities.map((r, i) => <li key={i}>{r}</li>)}
                    </ul>
                  </div>
                )}
                {result.requirements?.length > 0 && (
                  <div>
                    <p className="font-semibold text-xs mb-1">{t("offers.ai.requirements")}</p>
                    <ul className="text-xs space-y-0.5 list-disc list-inside">
                      {result.requirements.map((r, i) => <li key={i}>{r}</li>)}
                    </ul>
                  </div>
                )}
                {result.nice_to_have?.length > 0 && (
                  <div>
                    <p className="font-semibold text-xs mb-1">{t("offers.ai.niceToHave")}</p>
                    <ul className="text-xs space-y-0.5 list-disc list-inside">
                      {result.nice_to_have.map((r, i) => <li key={i}>{r}</li>)}
                    </ul>
                  </div>
                )}
                {result.benefits?.length > 0 && (
                  <div>
                    <p className="font-semibold text-xs mb-1">{t("offers.ai.benefits")}</p>
                    <ul className="text-xs space-y-0.5 list-disc list-inside">
                      {result.benefits.map((r, i) => <li key={i}>{r}</li>)}
                    </ul>
                  </div>
                )}
                {result.keywords?.length > 0 && (
                  <div>
                    <p className="font-semibold text-xs mb-1">{t("offers.ai.seoKeywords")}</p>
                    <div className="flex flex-wrap gap-1">
                      {result.keywords.map((k, i) => (
                        <span key={i} className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ backgroundColor: "var(--lm-s2)", color: "var(--lm-ink-2)" }}>
                          {k}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="px-6 py-3 border-t flex items-center justify-end gap-2" style={{ borderColor: "var(--lm-border)" }}>
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm"
            style={{ backgroundColor: "var(--lm-s1)", color: "var(--lm-ink-2)", border: "1px solid var(--lm-border)" }}>
            {t("common.cancel")}
          </button>
          <button onClick={() => onApply(result)} disabled={!result}
            className="px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-50"
            style={{ backgroundColor: "#5e6ad2" }}>
            {t("offers.ai.apply")}
          </button>
        </div>
      </div>
    </div>
  );
}

function ActionBtn({ icon: Icon, title, onClick, color }) {
  return (
    <button
      title={title}
      onClick={onClick}
      className="p-2 rounded-lg transition-all"
      style={{ color, backgroundColor: "transparent" }}
      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "var(--lm-s2)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; }}
    >
      <Icon className="h-4 w-4" />
    </button>
  );
}

const QUESTION_TYPE_VALUES = ["text", "textarea", "select", "boolean"];

const EMPTY_Q = { label: "", question_type: "text", options: [], required: false, order: 0 };

function QuestionsModal({ posting, onClose }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [form, setForm] = useState(EMPTY_Q);
  const [editingQ, setEditingQ] = useState(null);
  const [optionInput, setOptionInput] = useState("");

  const { data: questions = [] } = useQuery({
    queryKey: ["questions", posting.id],
    queryFn: () => recruitmentAPI.listQuestions(posting.id).then((r) => r.data),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["questions", posting.id] });

  const saveMut = useMutation({
    mutationFn: (data) => editingQ
      ? recruitmentAPI.updateQuestion(posting.id, editingQ.id, data)
      : recruitmentAPI.createQuestion(posting.id, data),
    onSuccess: () => { invalidate(); resetForm(); },
  });

  const deleteMut = useMutation({
    mutationFn: (qId) => recruitmentAPI.deleteQuestion(posting.id, qId),
    onSuccess: invalidate,
  });

  const resetForm = () => { setForm({ ...EMPTY_Q, order: questions.length }); setEditingQ(null); setOptionInput(""); };

  const startEdit = (q) => {
    setEditingQ(q);
    setForm({ label: q.label, question_type: q.question_type, options: q.options || [], required: q.required, order: q.order });
    setOptionInput("");
  };

  const addOption = () => {
    const v = optionInput.trim();
    if (v && !form.options.includes(v)) {
      setForm((f) => ({ ...f, options: [...f.options, v] }));
      setOptionInput("");
    }
  };

  const removeOption = (o) => setForm((f) => ({ ...f, options: f.options.filter((x) => x !== o) }));
  const canSaveQuestion = form.label.trim() && (form.question_type !== "select" || form.options.length > 0);

  const submitQuestion = () => {
    if (!form.label.trim()) return;
    if (form.question_type === "select" && form.options.length === 0) {
      toast.error(t("offers.questions.optionRequired"));
      return;
    }
    saveMut.mutate({ ...form, order: editingQ ? form.order : questions.length });
  };

  const inputStyle = { backgroundColor: "var(--lm-s1)", border: "1px solid var(--lm-border)", color: "var(--lm-ink)", borderRadius: "8px", padding: "6px 10px", fontSize: "12px", width: "100%", outline: "none" };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: "rgba(0,0,0,0.55)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-lg rounded-2xl shadow-2xl flex flex-col" style={{ backgroundColor: "var(--lm-s0)", border: "1px solid var(--lm-border)", maxHeight: "90vh" }}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: "1px solid var(--lm-border)" }}>
          <div>
            <h2 className="text-sm font-semibold" style={{ color: "var(--lm-ink)" }}>{t("offers.questions.title", { title: posting.title })}</h2>
            <p className="text-xs mt-0.5" style={{ color: "var(--lm-ink-3)" }}>{t("offers.questions.subtitle")}</p>
          </div>
          <button onClick={onClose} style={{ color: "var(--lm-ink-3)" }}><X className="h-4 w-4" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Existing questions */}
          {questions.length > 0 && (
            <div className="space-y-2">
              {questions.map((q) => (
                <div key={q.id} className="flex items-start gap-3 px-3 py-2.5 rounded-lg" style={{ backgroundColor: "var(--lm-s2)", border: "1px solid var(--lm-border)" }}>
                  <GripVertical className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" style={{ color: "var(--lm-ink-3)" }} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium" style={{ color: "var(--lm-ink)" }}>{q.label}</p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span className="text-xs px-1.5 py-0.5 rounded" style={{ backgroundColor: "rgba(94,106,210,0.12)", color: "#5e6ad2" }}>
                        {t(`offers.questions.types.${q.question_type}`, { defaultValue: q.question_type })}
                      </span>
                      {q.required && <span className="text-xs" style={{ color: "#dc2626" }}>{t("offers.questions.required")}</span>}
                      {q.options?.length > 0 && <span className="text-xs" style={{ color: "var(--lm-ink-3)" }}>{q.options.join(" · ")}</span>}
                    </div>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <button onClick={() => startEdit(q)} className="p-1 rounded" style={{ color: "var(--lm-ink-3)" }}><Pencil className="h-3.5 w-3.5" /></button>
                    <button onClick={() => deleteMut.mutate(q.id)} className="p-1 rounded" style={{ color: "#dc2626" }}><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Add / Edit form */}
          <div className="rounded-xl p-4 space-y-3" style={{ backgroundColor: "var(--lm-s2)", border: "1px solid var(--lm-border)" }}>
            <p className="text-xs font-semibold" style={{ color: "var(--lm-ink)" }}>{editingQ ? t("offers.questions.editQuestion") : t("offers.questions.addQuestion")}</p>

            <div>
              <label className="block text-xs mb-1" style={{ color: "var(--lm-ink-2)" }}>{t("offers.questions.questionLabel")}</label>
              <input value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))} placeholder={t("offers.questions.questionPh")} style={inputStyle} />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs mb-1" style={{ color: "var(--lm-ink-2)" }}>{t("offers.questions.type")}</label>
                <select value={form.question_type} onChange={(e) => setForm((f) => ({ ...f, question_type: e.target.value, options: [] }))} style={inputStyle}>
                  {QUESTION_TYPE_VALUES.map((v) => <option key={v} value={v}>{t(`offers.questions.types.${v}`)}</option>)}
                </select>
              </div>
              <div className="flex items-end pb-1">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.required} onChange={(e) => setForm((f) => ({ ...f, required: e.target.checked }))} className="w-3.5 h-3.5" />
                  <span className="text-xs" style={{ color: "var(--lm-ink-2)" }}>{t("offers.questions.required")}</span>
                </label>
              </div>
            </div>

            {form.question_type === "select" && (
              <div>
                <label className="block text-xs mb-1" style={{ color: "var(--lm-ink-2)" }}>{t("offers.questions.options")}</label>
                <div className="flex gap-2 mb-2">
                  <input value={optionInput} onChange={(e) => setOptionInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addOption(); } }}
                    placeholder={t("offers.questions.addOptionPh")} style={{ ...inputStyle, flex: 1 }} />
                  <button onClick={addOption} className="px-3 py-1 rounded-lg text-xs" style={{ backgroundColor: "rgba(94,106,210,0.12)", color: "#5e6ad2" }}>+</button>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {form.options.map((o) => (
                    <span key={o} className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs" style={{ backgroundColor: "rgba(94,106,210,0.12)", color: "#5e6ad2" }}>
                      {o}
                      <button onClick={() => removeOption(o)} className="ml-0.5"><X className="h-2.5 w-2.5" /></button>
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-1">
              {editingQ && (
                <button onClick={resetForm} className="px-3 py-1.5 rounded-lg text-xs" style={{ backgroundColor: "var(--lm-s1)", color: "var(--lm-ink-2)", border: "1px solid var(--lm-border)" }}>
                  {t("common.cancel")}
                </button>
              )}
              <button
                onClick={submitQuestion}
                disabled={!canSaveQuestion || saveMut.isPending}
                className="px-3 py-1.5 rounded-lg text-xs font-medium disabled:opacity-50"
                style={{ backgroundColor: "#5e6ad2", color: "#fff" }}
              >
                {saveMut.isPending ? t("offers.questions.saving") : editingQ ? t("common.save") : t("common.add")}
              </button>
            </div>
          </div>
        </div>

        <div className="flex justify-end px-5 py-4" style={{ borderTop: "1px solid var(--lm-border)" }}>
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-xs font-medium" style={{ backgroundColor: "var(--lm-s2)", color: "var(--lm-ink-2)", border: "1px solid var(--lm-border)" }}>
            {t("offers.questions.close")}
          </button>
        </div>
      </div>
    </div>
  );
}
